import {
    DocumentCard,
    DocumentCardDetails,
    DocumentCardPreview,
    DocumentCardTitle,
    DocumentCardType,
    IStackTokens,
    Stack,
} from '@fluentui/react';
import React, { useContext, useEffect, useState } from 'react';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { convertToUserFriendlyByteString } from '../../../../Common/util/tableUtils';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { Build } from '../../../models/Build';
import {
    documentCardStyles,
    previewPropsUsingIcon,
} from '../../../util/StyleUtils';
import { CloudEnvironment } from '../../../../Common/models/CloudEnvironment';

interface IBuildMetricsProps {
    data: Build | undefined;
}

export const BuildMetrics: React.FC<IBuildMetricsProps> = (
    props: IBuildMetricsProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const sectionStackTokens: IStackTokens = { childrenGap: 20 };
    // Use Set to store unique environmentIds
    const [uniqueEnvironmentId, setUniqueEnvironmentId] = useState<
        Set<CloudEnvironment | undefined>
    >(new Set());
    const [uniqueReleaseCorrelationId, setUniqueReleaseCorrelationId] =
        useState<Set<string | undefined>>(new Set());

    useEffect(() => {
        let tempEnvironmentId = new Set<CloudEnvironment | undefined>();
        let tempReleaseCorrelationId = new Set<string | undefined>();

        if (props.data) {
            props.data.artifacts.forEach((artifact) => {
                tempEnvironmentId.add(artifact.environmentId);
                tempReleaseCorrelationId.add(artifact.releaseCorrelationId);
            });

            setUniqueEnvironmentId(tempEnvironmentId);
            setUniqueReleaseCorrelationId(tempReleaseCorrelationId);
        }
    }, [props.data]);

    return (
        <div className="BuildMetrics-root">
            {props.data && (
                <Stack wrap horizontal tokens={sectionStackTokens}>
                    <DocumentCard
                        aria-label="Total Files"
                        title="Total Files"
                        type={DocumentCardType.normal}
                        className={documentCardStyles(theme).card}
                    >
                        <DocumentCardPreview
                            {...previewPropsUsingIcon('Documentation', theme)}
                        />
                        <DocumentCardDetails>
                            <DocumentCardTitle
                                title={
                                    'Total Files: ' +
                                    props.data.numReplicationPlanFiles
                                }
                                className={documentCardStyles(theme).title}
                            />
                        </DocumentCardDetails>
                    </DocumentCard>
                    <DocumentCard
                        aria-label="Total Bytes"
                        title="Total Bytes"
                        type={DocumentCardType.normal}
                        className={documentCardStyles(theme).card}
                    >
                        <DocumentCardPreview
                            {...previewPropsUsingIcon('PieDouble', theme)}
                        />
                        <DocumentCardDetails>
                            <DocumentCardTitle
                                title={
                                    'Total Bytes: ' +
                                    convertToUserFriendlyByteString(
                                        props.data.totalArtifactBytes
                                    )
                                }
                                className={documentCardStyles(theme).title}
                            />
                        </DocumentCardDetails>
                    </DocumentCard>
                    <DocumentCard
                        aria-label="Environment Count"
                        title="Environment Count"
                        type={DocumentCardType.normal}
                        className={documentCardStyles(theme).card}
                    >
                        <DocumentCardPreview
                            {...previewPropsUsingIcon('GlobeFavorite', theme)}
                        />
                        <DocumentCardDetails>
                            <DocumentCardTitle
                                title={
                                    'Environment Count: ' +
                                    uniqueEnvironmentId.size
                                }
                                className={documentCardStyles(theme).title}
                            />
                        </DocumentCardDetails>
                    </DocumentCard>
                    <DocumentCard
                        aria-label="Distinct Releases"
                        title="Distinct Releases"
                        type={DocumentCardType.normal}
                        className={documentCardStyles(theme).card}
                    >
                        <DocumentCardPreview
                            {...previewPropsUsingIcon('ProductRelease', theme)}
                        />
                        <DocumentCardDetails>
                            <DocumentCardTitle
                                title={
                                    'Distinct Releases: ' +
                                    uniqueReleaseCorrelationId.size
                                }
                                className={documentCardStyles(theme).title}
                            />
                        </DocumentCardDetails>
                    </DocumentCard>
                    <DocumentCard
                        aria-label="Total Replications"
                        title="Total Replications"
                        type={DocumentCardType.normal}
                        className={documentCardStyles(theme).card}
                    >
                        <DocumentCardPreview
                            {...previewPropsUsingIcon('Copy', theme)}
                        />
                        <DocumentCardDetails>
                            <DocumentCardTitle
                                title={
                                    'Total Replications: ' +
                                    props.data.artifacts.length
                                }
                                className={documentCardStyles(theme).title}
                            />
                        </DocumentCardDetails>
                    </DocumentCard>
                </Stack>
            )}
        </div>
    );
};
